import { HorizontalProductCard } from "@shared/ui.components";
import type {
  MultipleProductCard,
  SingularProductCard,
  MultipleProductCardVariant,
  VariantType,
  SingularProductCardVariant
} from "@shared/graphql.types";

import { ShadeSelectorDrawer } from "./ShadeSelectorDrawer";
import { useProductCard } from "./hooks/useProductCard";
import type { ProductCardTrackingPayload } from "./types";

export enum CtaButtonAction {
  ADD_TO_BASKET = "addToBasket",
  SELECT_SHADE = "selectShade"
}

type HorizontalProductCardWrapperProps = {
  productCard: SingularProductCard | MultipleProductCard;
  location: string;
  ctaButtonAction?: CtaButtonAction;
  styles?: {
    variant?: "standard" | "carousel";
    buttonVariant?: "primary" | "secondary";
  };
  addToBasketOptions?: {
    afterAdd?: (variant: VariantType) => void;
    beforeAdd?: () => void;
  };
  onClickOptions?: {
    productClickedTrackingPayload?: ProductCardTrackingPayload;
    productCardClick?: () => void;
    disableLink?: boolean;
  };
  shouldTrackProductImpression?: boolean;
};

const hasSomeVariantsInStock = (
  product: SingularProductCard | MultipleProductCard
) => {
  return product.variants.some(
    (variant: SingularProductCardVariant | MultipleProductCardVariant) =>
      variant.availability.inStock
  );
};

export const HorizontalProductCardWrapper = ({
  productCard,
  location,
  onClickOptions,
  styles: { variant, buttonVariant } = {},
  ctaButtonAction = CtaButtonAction.ADD_TO_BASKET,
  addToBasketOptions,
  shouldTrackProductImpression = true
}: HorizontalProductCardWrapperProps) => {
  const {
    productCardData,
    activeVariant,
    isShadeSelectorOpen,
    handlers,
    shouldRenderShadeSelector,
    inViewRef
  } = useProductCard(productCard, {
    location,
    addToBasketOptions,
    ctaButtonAction,
    enableShadeSelector: true,
    productClickedTrackingPayload:
      onClickOptions?.productClickedTrackingPayload,
    productCardClick: onClickOptions?.productCardClick,
    shouldTrackProductImpression
  });

  return (
    <>
      {shouldRenderShadeSelector && (
        <ShadeSelectorDrawer
          product={productCard as MultipleProductCard}
          activeVariant={activeVariant as MultipleProductCardVariant}
          isOpen={isShadeSelectorOpen}
          onClose={handlers.closeShadeSelector}
          onShadeSelect={handlers.selectShade}
          resultId={onClickOptions?.productClickedTrackingPayload?.resultId}
          location={location}
          onATB={handlers.addToBasket}
        />
      )}

      <HorizontalProductCard
        variant={variant ?? "standard"}
        buttonVariant={buttonVariant ?? "primary"}
        product={{
          ...productCardData,
          isPrimaryButtonDisabled:
            ctaButtonAction === CtaButtonAction.SELECT_SHADE
              ? hasSomeVariantsInStock(productCard) === false
              : productCardData.isPrimaryButtonDisabled
        }}
        onCardClick={handlers.onCardClick}
        onButtonClick={handlers.ctaButton}
        onShadeSelectorClick={handlers?.onShadeSelectorClick}
        onWishlistToggle={handlers.toggleWishlist}
        disableLink={onClickOptions?.disableLink}
        ref={inViewRef}
      />
    </>
  );
};
